import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SectionHeading, Reveal } from "@/components/Primitives";
import api, { rupee } from "@/lib/api";
import { UserPlus, Share2, LineChart, BadgeIndianRupee, Trophy, ShieldCheck, ArrowRight } from "lucide-react";

const STEPS = [
  { icon: UserPlus, t: "Register", d: "Sign up as a referral partner and accept the referral terms." },
  { icon: Share2, t: "Share", d: "Share your unique link/code, or introduce a business with its permission." },
  { icon: LineChart, t: "Track", d: "Watch each referral progress through clear, privacy-safe stages." },
  { icon: BadgeIndianRupee, t: "Earn", d: "Earn commission once payment is verified and admin approves." },
];

export default function ReferEarn() {
  const [lb, setLb] = useState({ enabled: false, entries: [] });
  const [settings, setSettings] = useState({});
  useEffect(() => {
    api.get("/public/leaderboard").then((r) => setLb(r.data)).catch(() => {});
    api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <div className="text-center max-w-3xl mx-auto mb-14">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs text-cyan-200 mb-5"><BadgeIndianRupee className="w-3.5 h-3.5" /> Partner Programme</div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white leading-tight">Refer a Business. <span className="text-gradient">Earn When Their Project Qualifies.</span></h1>
        <p className="mt-5 text-slate-300 leading-relaxed">Introduce local businesses to Nova Web Solutions and earn a fixed commission once their website project is won and the required payment is verified.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
          <Link to="/partner/register"><Button size="lg" className="bg-violet-600 hover:bg-violet-500 text-white font-semibold btn-glow h-12 px-7">Become a Partner <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
          <Link to="/login?role=partner"><Button size="lg" variant="outline" className="border-white/15 bg-transparent text-white hover:bg-white/10 h-12 px-7">Partner Login</Button></Link>
        </div>
        {!settings.referral_published && (
          <p className="text-xs text-amber-300/90 bg-amber-500/10 rounded-lg px-4 py-2.5 mt-6 inline-block">The programme terms and rates are being finalised. You can register now; commissions apply per the confirmed terms.</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-16">
        {STEPS.map((s, i) => (
          <Reveal key={s.t} delay={i * 0.08}>
            <div className="glass rounded-2xl p-6 card-hover h-full">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-violet-500/20 text-violet-300 mb-4"><s.icon className="w-5 h-5" /></span>
              <div className="text-xs font-mono text-cyan-300 mb-1">Step {i + 1}</div>
              <h3 className="font-semibold text-white mb-2">{s.t}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{s.d}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="glass-strong rounded-3xl p-8">
          <div className="flex items-center gap-2 mb-4"><ShieldCheck className="w-5 h-5 text-emerald-400" /><h3 className="font-display text-xl font-bold text-white">How commissions work</h3></div>
          <ul className="space-y-3 text-sm text-slate-300">
            <li>• Commission is paid per eligible converted project — <b>never</b> just for submitting a contact.</li>
            <li>• Eligibility: the project is marked Won, the required full payment is verified, any holding period ends, and admin approves.</li>
            <li>• Your applicable rate is snapshotted when your referral is accepted, so later rate changes won't reduce your entitlement.</li>
            <li>• Minimum payout is {settings.payout_threshold_rupees ? rupee(settings.payout_threshold_rupees) : "₹1,000"}. We never ask for UPI PINs, bank passwords or OTPs.</li>
          </ul>
          <Link to="/legal/referral-terms"><Button variant="link" className="text-cyan-300 px-0 mt-3">Read full referral terms →</Button></Link>
        </div>

        <div className="glass-strong rounded-3xl p-8">
          <div className="flex items-center gap-2 mb-4"><Trophy className="w-5 h-5 text-amber-400" /><h3 className="font-display text-xl font-bold text-white">Partner leaderboard</h3></div>
          {lb.enabled ? (
            lb.entries.length ? (
              <div className="space-y-2">
                {lb.entries.map((e, i) => (
                  <div key={i} className="flex items-center justify-between glass rounded-xl px-4 py-3">
                    <span className="flex items-center gap-3 text-white"><span className="font-mono text-amber-400 w-5">{i + 1}</span>{e.display_name}</span>
                    <span className="text-sm text-slate-400">{e.converted} converted</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-400">No opted-in partners yet.</p>
          ) : (
            <div className="text-center py-8">
              <p className="text-sm text-slate-400">The public leaderboard is currently disabled. It's opt-in only and shows converted referral counts with chosen display names — never contact details or income.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
